import { AgGridAngular } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    Output,
    EventEmitter,
    OnChanges, SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { MemoizedSelector, select, Store } from '@ngrx/store';
import { CompanySubset } from 'app/api/company.api.service';
import { CubeEntry } from 'app/api/cube.api.service';
import { AppState } from 'app/reducers';
import {
    agGridLocale,
    EurCurrencyCellRenderer, EuroKCellRenderer,
    NoDecimalCellRenderer,
} from 'app/utils/ag-grid.locale';
import { AmplifyService } from 'aws-amplify-angular';
import { Observable, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { CompaniesSelectors, CompaniesUISelectors } from '../company/selectors';
import { CubeDetailDialogComponent } from './cube-detail/cube-detail-dialog.component';
import {
    selectCubeAnalytics,
    selectCubeForAccount6,
    selectCubeForAccount7,
    selectCubeForBanks,
    selectCubeForEquity,
    selectCubeForExpenses,
    selectCubeForExpensesByCategory,
    selectCubeForInvest,
    selectCubeForRevenue,
    selectCubeForRevenueByCategory,
    selectCubeForThirdParty,
    selectCubeLoading,
    selectCubeForResultat,
    selectCubeForThirdParty2,
} from './cube.selectors';

import * as _ from 'lodash';

export interface CubeRow extends CubeEntry {
    movement?: string;
    gen2Label?: string;
    thirdParty?: string;
    rac1?: string;
    rac2?: string;
    rac3?: string;
    missingRule?: boolean;
    ana1Name?: string;
    ana2Name?: string;
    ana3Name?: string;
    paymentPeriod?: string;
}

@Component({
    selector: 'cube',
    templateUrl: './cube.component.html',
    styleUrls: ['./cube.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
})
export class CubeComponent implements OnInit, OnDestroy, AfterViewInit {
    public gridApi;
    public gridColumnApi;
    public columnDefs;
    public defaultColDef;
    public autoGroupColumnDef;
    public sideBar;
    public statusBar;
    public rowBuffer;
    public localeText;
    public rowClassRules;
    public pivotMode;
    public modules;
    public popupParent;

    // Route data
    public pageTitle;
    private viewType;
    viewTypeEuro: 'euro' | 'eurok';
    keyUp = new Subject<KeyboardEvent>();
    rowData$: Observable<CubeRow[]>;
    loading$: Observable<boolean>;

    gridViewCtrl: FormControl;
    // Protected
    protected logger: any;

    // Private
    private company: CompanySubset;
    private _unsubscribeAll: Subject<any>;

    @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;
    @Output() viewTypeChanged = new EventEmitter();
    constructor(
        @Inject(DOCUMENT) private document: any,
        protected amplifyService: AmplifyService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected store: Store<AppState>,
        public dialog: MatDialog
    ) {
        this.logger = this.amplifyService.logger('CubeComponent');
        this._unsubscribeAll = new Subject();
        this.modules = AllModules;
        this.pageTitle = activatedRoute.snapshot.data['title'];
        this.viewType = activatedRoute.snapshot.data['view'];

        this.gridViewCtrl = new FormControl();
        this.popupParent = document.getElementById('body');
        this.localeText = agGridLocale.fr;

        this.setColumnDefs(this.viewType);
        this.setDefaultColDef(this.viewType);
        this.autoGroupColumnDef = {
            width: 360,
            pinned: 'left',
        };

        this.sideBar =
            this.viewType === 'grid'
                ? {
                      toolPanels: [
                          {
                              id: 'columns',
                              labelDefault: 'Colonnes',
                              labelKey: 'columns',
                              iconKey: 'columns',
                              toolPanel: 'agColumnsToolPanel',
                          },
                          {
                              id: 'filters',
                              labelDefault: 'Filtres',
                              labelKey: 'filters',
                              iconKey: 'filter',
                              toolPanel: 'agFiltersToolPanel',
                          },
                      ],
                  }
                : null;
        this.statusBar = {
            statusPanels: [
                {
                    statusPanel: 'agTotalRowCountComponent',
                    align: 'left',
                },
                {
                    statusPanel: 'agAggregationComponent',
                },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agFilteredRowCountComponent' },
            ],
        };
        this.pivotMode =
            ['grid', 'providers-view', 'customers-view'].indexOf(
                this.viewType
            ) < 0;
    }

    ngOnInit(): void {
        this.loading$ = this.store.pipe(select(selectCubeLoading));

        this.keyUp
            .pipe(
                takeUntil(this._unsubscribeAll),
                map((event: any) => event.target.value),
                debounceTime(250),
                distinctUntilChanged(),
                tap((search) => this.gridApi.setQuickFilter(search))
            )
            .subscribe();

        this.setRowData(this.viewType);

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(CompaniesSelectors.selectCurrentCompany)
            )
            .subscribe((company) => (this.company = company));

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(CompaniesUISelectors.selectCurrentCompanyId)
            )
            .subscribe((companyId) => {
                window.history.pushState(
                    {},
                    '',
                    `/cube/${companyId}/${this.viewType}`
                );
            });
    }

  setCellRender(
    viewTypeEuro: string,
  ): void {
        const indeFl = _.findIndex(this.columnDefs, { field: 'flux'} );
        console.log(this.columnDefs);
        const cellRenderer = {
        eurok: EuroKCellRenderer,
        euro: NoDecimalCellRenderer,
    };
      // tslint:disable-next-line:no-unused-expression
        this.columnDefs[indeFl].cellRenderer = cellRenderer[viewTypeEuro];
        this.gridApi.setColumnDefs(this.columnDefs);

    // selectViewTypeSynthese

        setTimeout(() => this.autoSizeAll(this.agGrid));
  }
    ngAfterViewInit(): void {}

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    onGridReady(params): void {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
    }

    public clearSearch(): void {
        this.gridApi.setQuickFilter('');
    }

    onFirstDataRendered(params): void {
        this.autoSizeAll(params);
    }

    onCellDoubleClicked(event): void {
        const data: {
            company: CompanySubset;
            title: string;
            labels: string[];
            rows: CubeRow[];
        } = {
            company: this.company,
            title: this.pageTitle,
            labels: [],
            rows: [],
        };
        if (event.node && event.node.allLeafChildren) {
            data.labels = this.getLabelsFromNode(event.node);
            if (event.colDef.pivotKeys && event.colDef.pivotKeys.length === 1) {
                const pivot = event.colDef.pivotKeys[0];
                data.labels.push(`(${pivot})`);
                data.rows = event.node.allLeafChildren
                    .filter((node) => node.data && node.data.timeline === pivot)
                    .map((node) => node.data);
            } else {
                data.rows = event.node.allLeafChildren.map((node) => node.data);
            }
            this.dialog.open(CubeDetailDialogComponent, {
                maxWidth: '95vw',
                maxHeight: '95vh',
                width: '85vw',
                height: '85vh',
                // panelClass: 'dialog-container-no-padding',
                data,
            });
        }
    }

    private getLabelsFromNode(node: any, labels: string[] = []): string[] {
        if (node && node.key) {
            labels.unshift(node.key);
            if (node.level !== 0) {
                labels = this.getLabelsFromNode(node.parent, labels);
            }
        }
        return labels;
    }

    private autoSizeAll(params): void {
        const allColumnIds = params.columnApi
            .getAllColumns()
            .map((column) => column.colId);
        setTimeout(() => params.columnApi.autoSizeColumns(allColumnIds), 0);
    }

    private setColumnDefs(viewType: string): void {
        switch (viewType) {
            case 'resultat':
                this.columnDefs = [
                    {
                        headerName: 'Timeline',
                        field: 'timeline',
                        width: 140,
                        pivot: true,
                    },
                    { headerName: 'ANA2', field: 'ana2Name', rowGroupIndex: 0 },
                    { headerName: 'ANA3', field: 'ana3Name', rowGroupIndex: 1 },
                    // {
                    //     headerName: 'Client',
                    //     field: 'customerLabel',
                    //     rowGroupIndex: 2,
                    // },
                    // {
                    //     headerName: 'Fournisseur',
                    //     field: 'providerLabel',
                    //     rowGroupIndex: 2,
                    // },
                    {
                        headerName: 'Tiers2', field: 'thirdParty2', rowGroupIndex: 2
                    }
                ];
                break;
            case 'revenue':
                this.columnDefs = [
                    {
                        headerName: 'Timeline',
                        field: 'timeline',
                        width: 140,
                        pivot: true,
                    },
                    { headerName: 'ANA2', field: 'ana2Name', rowGroupIndex: 0 },
                    { headerName: 'ANA3', field: 'ana3Name', rowGroupIndex: 1 },
                    {
                        headerName: 'Client',
                        field: 'customerLabel',
                        rowGroupIndex: 2,
                    },
                ];
                break;
            case 'revenue-by-category':
                this.columnDefs = [
                    {
                        headerName: 'Timeline',
                        field: 'timeline',
                        width: 140,
                        pivot: true,
                    },
                    {
                        headerName: 'Catégorie',
                        field: 'customerCategory',
                        rowGroupIndex: 0,
                    },
                    {
                        headerName: 'Client',
                        field: 'customerLabel',
                        rowGroupIndex: 1,
                    },
                ];
                break;
            case 'expenses':
                this.columnDefs = [
                    {
                        headerName: 'Timeline',
                        field: 'timeline',
                        width: 140,
                        pivot: true,
                    },
                    { headerName: 'ANA2', field: 'ana2Name', rowGroupIndex: 0 },
                    { headerName: 'ANA3', field: 'ana3Name', rowGroupIndex: 1 },
                    {
                        headerName: 'Fournisseur',
                        field: 'providerLabel',
                        rowGroupIndex: 2,
                    },
                ];
                break;
            case 'expenses-by-category':
                this.columnDefs = [
                    {
                        headerName: 'Timeline',
                        field: 'timeline',
                        width: 140,
                        pivot: true,
                    },
                    {
                        headerName: 'Catégorie',
                        field: 'providerCategory',
                        rowGroupIndex: 0,
                    },
                    {
                        headerName: 'Fournisseur',
                        field: 'providerLabel',
                        rowGroupIndex: 1,
                    },
                ];
                break;
            case 'equity':
            case 'invest':
                this.columnDefs = [
                    {
                        headerName: 'Timeline',
                        field: 'timeline',
                        width: 140,
                        pivot: true,
                    },
                    { headerName: 'ANA2', field: 'ana2Name', rowGroupIndex: 0 },
                    { headerName: 'ANA3', field: 'ana3Name', rowGroupIndex: 1 },
                ];
                break;
            case 'third-party':
                this.columnDefs = [
                    { headerName: 'ANA2', field: 'ana2Name', rowGroupIndex: 0 },
                    { headerName: 'Tiers', field: 'thirdParty', rowGroupIndex: 1 }
                ];
                break;
            case 'third-party-2':
                this.columnDefs = [
                    {
                        headerName: 'Tiers',
                        field: 'timeline',
                        width: 140,
                        pivot: true,
                    },
                    { headerName: 'ANA2', field: 'ana2Name', rowGroupIndex: 0 },
                    { headerName: 'ANA3', field: 'ana3Name', rowGroupIndex: 1 },
                ];
                break;

            case 'cash-burn':
                this.columnDefs = [];
                break;
            case 'banks':
                this.columnDefs = [
                    {
                        headerName: 'Timeline',
                        field: 'timeline',
                        width: 140,
                        pivot: true,
                    },
                    {
                        headerName: 'Mouvement Cash',
                        field: 'movement',
                        rowGroupIndex: 0,
                    },
                    {
                        headerName: 'Gen2',
                        field: 'gen2Label',
                        rowGroupIndex: 1,
                    },
                    {
                        headerName: 'Libellé écriture',
                        field: 'entryLabel',
                        rowGroupIndex: 2,
                    },
                ];
                break;
            case 'providers-view':
                this.columnDefs = [
                    { headerName: 'ANA2', field: 'ana2Name' },
                    { headerName: 'ANA3', field: 'ana3Name' },
                    { headerName: 'Journal', field: 'journal' },
                    { headerName: 'Compte', field: 'account' },
                    { headerName: 'Lib Compte', field: 'accountLabel' },
                    { headerName: 'Date', field: 'entryDate' },
                    {
                        headerName: 'Libellé',
                        field: 'entryLabel',
                        editable: true,
                    },
                    {
                        headerName: 'Flux',
                        field: 'flux',
                        width: 160,
                        cellStyle: { 'text-align': 'right' },
                        cellClass: (params) => {
                            return params.data && params.data.flux < 0
                                ? 'negative-flux'
                                : null;
                        },
                        cellRenderer: EurCurrencyCellRenderer,
                    },
                    {
                        headerName: 'Fournisseur',
                        field: 'providerLabel',
                        cellClass: (params) => {
                            return params.data && params.data.missingRule
                                ? 'missing-rule'
                                : null;
                        },
                    },
                    { headerName: 'Catégorie', field: 'providerCategory' },
                ];
                break;

            case 'customers-view':
                this.columnDefs = [
                    { headerName: 'ANA2', field: 'ana2Name' },
                    { headerName: 'ANA3', field: 'ana3Name' },
                    { headerName: 'Journal', field: 'journal' },
                    { headerName: 'Compte', field: 'account' },
                    { headerName: 'Lib Compte', field: 'accountLabel' },
                    { headerName: 'Date', field: 'entryDate' },
                    {
                        headerName: 'Libellé',
                        field: 'entryLabel',
                        editable: true,
                    },
                    {
                        headerName: 'Flux',
                        field: 'flux',
                        width: 160,
                        cellStyle: { 'text-align': 'right'},
                        cellClass: (params) => {
                            return params.data && params.data.flux < 0
                                ? 'negative-flux'
                                : null;
                        },
                        cellRenderer: EurCurrencyCellRenderer,
                    },
                    {
                        headerName: 'Client',
                        field: 'customerLabel',
                        cellClass: (params) => {
                            return params.data && params.data.missingRule
                                ? 'missing-rule'
                                : null;
                        },
                    },
                    { headerName: 'Catégorie', field: 'customerCategory' },
                ];
                break;

            default:
                this.columnDefs = [
                    { headerName: 'Date', field: 'entryDate' },
                    { headerName: 'Journal', field: 'journal', width: 60 },
                    { headerName: 'Libellé', field: 'entryLabel' },
                    {
                        headerName: 'Débit',
                        field: 'debit',
                        type: 'numericColumn',
                        hide: true,
                        cellRenderer: EurCurrencyCellRenderer,
                    },
                    {
                        headerName: 'Crédit',
                        field: 'credit',
                        type: 'numericColumn',
                        hide: true,
                        cellRenderer: EurCurrencyCellRenderer,
                    },
                    { headerName: 'Lettrage', field: 'lettering', width: 80 },
                    { headerName: 'Compte', field: 'account' },
                    { headerName: 'Lib Compte', field: 'accountLabel' },
                    { headerName: 'Auxiliaire', field: 'auxiliary' },
                    { headerName: 'Lib Cli/Frs', field: 'auxiliaryLabel' },
                    {
                        headerName: 'Flux',
                        field: 'flux',
                        width: 160,
                        cellStyle: { 'text-align': 'right' },
                        cellClass: (params) => {
                            return params.data && params.data.flux < 0
                                ? 'negative-flux'
                                : null;
                        },
                        cellRenderer: EurCurrencyCellRenderer,
                    },
                    { headerName: 'Mouvement', field: 'movement', width: 100 },
                    { headerName: 'Rac1', field: 'rac1', width: 80 },
                    { headerName: 'Rac2', field: 'rac2', width: 80 },
                    { headerName: 'Rac3', field: 'rac3', width: 80 },
                    { headerName: 'Timeline', field: 'timeline', width: 160 },
                    { headerName: 'Gen1', field: 'gen1', width: 280 },
                    { headerName: 'Gen2', field: 'gen2' },
                    { headerName: 'Gen3', field: 'gen3' },
                    { headerName: 'ANA1', field: 'ana1Name' },
                    { headerName: 'ANA2', field: 'ana2Name' },
                    { headerName: 'ANA3', field: 'ana3Name' },
                    { headerName: 'FRS Retenu', field: 'providerLabel' },
                    { headerName: 'FRS Famille', field: 'providerCategory' },
                    { headerName: 'CLI Retenu', field: 'customerLabel' },
                    { headerName: 'CLI Famille', field: 'customerCategory' },
                    { headerName: 'Tiers', field: 'thirdParty2', width: 280 },
                ];
                break;
        }

        if (
            ['grid', 'providers-view', 'customers-view', 'cash-burn'].indexOf(
                viewType
            ) < 0
        ) {
            this.columnDefs.push({
                headerName: 'Flux',
                field: 'flux',
                width: 100,
                cellStyle: { 'text-align': 'right' },
                cellClass: (params) => {
                    return params.value < 0 ? 'negative-flux' : null;
                },
                aggFunc: 'sum',
                cellRenderer: (params) => {
                    if (this.viewTypeEuro === 'eurok') {
                       return EuroKCellRenderer(params);
                    }
                    return NoDecimalCellRenderer(params);

            }
                // cellRenderer: EuroKCellRenderer,
            });
        }
    }

    private setDefaultColDef(viewType: string): void {
        if (
            ['grid', 'providers-view', 'customers-view'].indexOf(viewType) < 0
        ) {
            this.defaultColDef = {
                enableValue: false,
                enableRowGroup: false,
                enablePivot: false,
                sortable: true,
                filter: true,
                resizable: true,
                enableCharts: false,
            };
            return;
        }
        this.defaultColDef = {
            width: 150,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            sortable: true,
            filter: true,
            resizable: true,
            enableCharts: false,
        };
    }

    private setRowData(viewType: string): void {
        let query: MemoizedSelector<object, CubeRow[]>;
        switch (viewType) {
            case 'resultat':
                query = selectCubeForResultat;
                break;
            case 'revenue':
                query = selectCubeForRevenue;
                break;
            case 'revenue-by-category':
                query = selectCubeForRevenueByCategory;
                break;
            case 'expenses':
                query = selectCubeForExpenses;
                break;
            case 'expenses-by-category':
                query = selectCubeForExpensesByCategory;
                break;
            case 'equity':
                query = selectCubeForEquity;
                break;
            case 'invest':
                query = selectCubeForInvest;
                break;
            case 'third-party':
                query = selectCubeForThirdParty;
                break;
            case 'third-party-2':
                query = selectCubeForThirdParty2;
                break;
            case 'banks':
                query = selectCubeForBanks;
                break;
            case 'customers-view':
                query = selectCubeForAccount7;
                break;
            case 'providers-view':
                query = selectCubeForAccount6;
                break;

            default:
                query = selectCubeAnalytics;
                break;
        }

        this.rowData$ = this.store.pipe(select(query));
    }

    // saveGridView(): void {
    //     const gridView = {
    //         columnState: this.gridColumnApi.getColumnState(),
    //         isPivotMode: this.gridColumnApi.isPivotMode(),
    //         filterModel: this.gridApi.getFilterModel(),
    //         sortModel: this.gridApi.getSortModel(),
    //     };
    // }

    // onColumnPivotModeChanged(ev): void {
    //     console.log('onColumnPivotModeChanged', ev);
    //     this.autoSizeAll(ev);
    // }
    // onColumnEverythingChanged(ev): void {
    //     console.log('onColumnEverythingChanged', ev);
    // }
    // onDisplayedColumnsChanged(ev): void {
    //     console.log('onDisplayedColumnsChanged', ev);
    // }
    // onColumnPivotChanged(ev): void {
    //     console.log('onColumnPivotChanged', ev);
    //     this.autoSizeAll(ev);
    // }
    // onColumnRowGroupChanged(ev): void {
    //     console.log('onColumnRowGroupChanged', ev);
    //     this.autoSizeAll(ev);
    // }
    // onColumnValueChanged(ev): void {
    //     console.log('onColumnValueChanged', ev);
    //     this.autoSizeAll(ev);
    // }
}
